// src/app/features/auth/authApi.js
import http from "../../api/http.js";

function normalizeAuthResponse(data) {
  // Supports:
  // - { accessToken, raw: { user } }
  // - { access_token, user }
  // - legacy shapes that might include { accessToken, user }
  const accessToken = data?.accessToken || data?.access_token || data?.accessToken?.token || null;

  // Try to locate user in likely locations
  const user =
    data?.raw?.user ||
    data?.user ||
    data?.raw?.data?.user ||
    null;

  return {
    accessToken,
    raw: {
      ...data,
      // Ensure frontend has a consistent user location
      user: user || data?.raw?.user || data?.user || null,
    },
  };
}

function extractDetail(err) {
  const data = err?.response?.data;

  if (!data) return err?.message || "Request failed";

  if (typeof data?.detail === "string") return data.detail;

  // FastAPI validation errors are often arrays
  if (Array.isArray(data?.detail)) {
    return data.detail
      .map((d) => d?.msg || d?.message || d?.detail)
      .filter(Boolean)
      .join("; ");
  }

  // Sometimes backend returns { message: "..."}
  if (typeof data?.message === "string") return data.message;

  try {
    return JSON.stringify(data);
  } catch {
    return "Request failed";
  }
}

export async function login(identifier, password) {
  try {
    const res = await http.post("/auth/login", { identifier, password });
    
    console.log('🔍 Login API response:', res.data);
    
    let accessToken = res.data?.accessToken || res.data?.access_token || null;
    
    if (!accessToken) {
      throw new Error("No access token received");
    }
    
    // ✅ Try to get user from login response
    let user = res.data?.user || res.data?.raw?.user || null;
    
    console.log('🔍 User from login response:', user);
    
    // ✅ If user is not in response, fetch from /me
    if (!user && accessToken) {
      console.log('🔄 Fetching user from /me...');
      try {
        const userRes = await http.get("/auth/me", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        user = userRes.data?.user || userRes.data || null;
        console.log('✅ User from /me:', user);
      } catch (err) {
        console.error('❌ Failed to fetch user from /me:', err);
      }
    }
    
    // ✅ If still no user, create fallback
    if (!user) {
      console.warn('⚠️ No user found, creating fallback');
      user = {
        id: 'unknown',
        email: identifier,
        role: identifier === 'admin@worldcoinscom.com' ? 'ADMIN' : 'USER',
        kyc_status: 'VERIFIED',
        is_active: true,
        is_suspended: false,
        geo_allowed: true,
      };
    }
    
    // ✅ Force admin role for admin email
    if (user.email === 'admin@worldcoinscom.com' && user.role !== 'ADMIN') {
      console.log('🔧 Forcing ADMIN role');
      user.role = 'ADMIN';
    }
    
    console.log('✅ Final user object:', user);
    console.log('✅ User role:', user.role);
    
    return {
      accessToken,
      raw: {
        ...res.data,
        user: user,
      },
    };
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}

export async function getCurrentUser() {
  try {
    const res = await http.get("/auth/me");
    return res.data?.user || res.data || null;
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}

export async function refreshUser() {
  try {
    const user = await getCurrentUser();
    if (user) {
      // Force admin role if admin email
      if (user.email === 'admin@worldcoinscom.com') {
        user.role = 'ADMIN';
      }
      
      // Update stored user
      const session = JSON.parse(localStorage.getItem('session') || '{}');
      session.user = user;
      localStorage.setItem('session', JSON.stringify(session));
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('auth:changed'));
    }
    return user;
  } catch (err) {
    console.error('Error refreshing user:', err);
    throw err;
  }
}

export async function register(payload) {
  try {
    const res = await http.post("/auth/register", payload);
    return res.data;
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}

export async function forgotPassword(email) {
  try {
    const res = await http.post("/auth/forgot-password", { email });
    return res.data;
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}

export async function verifyResetToken(token) {
  try {
    const res = await http.post("/auth/verify-reset-token", { token });
    return res.data;
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}

export async function resetPassword(token, new_password) {
  try {
    const res = await http.post("/auth/reset-password", { token, new_password });
    return res.data;
  } catch (err) {
    err.message = extractDetail(err);
    throw err;
  }
}
